import React from "react";
import { Card, CardContent } from "../../ui/card";
import { Headline, Kpi, PainBanner, Pill, SectionTitle } from "./PersonaUi";
import { OBS_F, RF, SHORT, usd } from "./riaFundsData";

const LIMIT = 0.1;

export default function RiaHousehold() {
  const H = RF.household;
  const L = H.look;
  const top = L.top[0];
  const both = L.top.filter((r) => r.direct > 0 && r.via_funds > 1000);
  const excess = top.usd - LIMIT * L.total;
  const secs = L.sectors.filter((s) => !/unmapped/i.test(s.sector));
  const unm = L.sectors.filter((s) => /unmapped/i.test(s.sector)).reduce((a, s) => a + s.pct, 0);
  const tech = secs.find((s) => s.sector === "Technology");
  return (
    <div className="space-y-4" data-testid="persona-ria-household">
      <PainBanner testid="persona-ria-household-pain" persona="RIA · HOUSEHOLD LOOK-THROUGH"
        pain="My client owns four funds and a few stocks — what do they really own, and are they more concentrated than they think?"
        answer="every fund looked through to the underlying stocks, added to direct holdings" />
      <div>
        <Headline testid="ria-household-headline">{top.symbol} is {top.pct.toFixed(1)}% of the Smith household — {Math.round((top.via_funds / top.usd) * 100)}% of it comes through funds, not the direct position</Headline>
        <div className="text-sm text-muted-foreground mt-1">Sample household · {usd(L.total)} · 4 mutual funds + 4 stocks · real fund holdings (latest N-PORT per fund) as of {OBS_F}</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testid="ria-hh-kpi-total" label="Household" value={usd(L.total)} sub={`${usd(Object.values(H.funds).reduce((a, b) => a + b, 0))} funds · ${usd(Object.values(H.direct).reduce((a, b) => a + b, 0))} stocks`} />
        <Kpi testid="ria-hh-kpi-top" label="Largest single stock" value={`${top.pct.toFixed(1)}%`} tone="text-red-400" sub={`${top.symbol} · policy limit ${LIMIT * 100}%`} />
        <Kpi testid="ria-hh-kpi-top10" label="Top-10 stocks" value={`${L.top10_pct}%`} sub="of household" />
        <Kpi testid="ria-hh-kpi-double" label="Owned twice" value={both.length} tone="text-amber-400" sub={both.map((b) => b.symbol).join(", ")} />
        <Kpi testid="ria-hh-kpi-tech" label="Technology" value={`${tech ? tech.pct.toFixed(1) : "—"}%`} sub="look-through sector weight" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 items-start">
        <Card className="xl:col-span-3"><CardContent className="pt-4">
          <SectionTitle right={<span className="text-[10px] text-muted-foreground"><span className="inline-block w-2.5 h-2.5 bg-sky-500/80 rounded-sm mr-1" />direct <span className="inline-block w-2.5 h-2.5 bg-violet-400/70 rounded-sm mx-1" />via funds</span>}>What the household really owns</SectionTitle>
          <div className="space-y-2" data-testid="ria-hh-table">
            {L.top.map((r) => (
              <div key={r.symbol} className="grid grid-cols-12 items-center gap-2 text-xs" data-testid={`ria-hh-row-${r.symbol}`}>
                <span className="col-span-1 font-mono font-bold">{r.symbol}</span>
                <div className="col-span-7 h-3 rounded bg-zinc-800 overflow-hidden flex">
                  <div className="h-full bg-sky-500/80" style={{ width: `${(r.direct / L.total) * 100 * 5}%` }} />
                  <div className="h-full bg-violet-400/70" style={{ width: `${(r.via_funds / L.total) * 100 * 5}%` }} />
                </div>
                <span className={`col-span-1 text-right font-mono ${r.pct >= LIMIT * 100 ? "text-red-400 font-bold" : ""}`}>{r.pct.toFixed(1)}%</span>
                <span className="col-span-2 text-right font-mono text-muted-foreground">{usd(r.usd)}</span>
                <span className="col-span-1 text-right text-[10px] text-muted-foreground">{r.n_funds} fd</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">Bar scale: full width = 20% of household. "fd" = number of the client's funds holding at least 0.5%.</div>
        </CardContent></Card>
        <div className="xl:col-span-2 space-y-4">
          <Card data-testid="ria-hh-action"><CardContent className="pt-4">
            <SectionTitle right={<Pill tone="alert">ACTION</Pill>}>Bring {top.symbol} under the {LIMIT * 100}% policy limit</SectionTitle>
            <div className="text-[13px] leading-relaxed">
              Sell about <b>{usd(excess)}</b> of the direct {top.symbol} position ({usd(H.direct[top.symbol])} today) — the client keeps {top.symbol} exposure through the funds
              ({usd(top.via_funds)}). Check tax lots before trading.
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <SectionTitle>Look-through sectors</SectionTitle>
            <div className="space-y-1.5" data-testid="ria-hh-sectors">
              {secs.slice(0, 8).map((s) => (
                <div key={s.sector} className="flex items-center gap-2 text-xs">
                  <span className="w-40 text-muted-foreground truncate">{s.sector}</span>
                  <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full bg-sky-500/70" style={{ width: `${(s.pct / 40) * 100}%` }} /></div>
                  <span className="font-mono w-12 text-right">{s.pct.toFixed(1)}%</span>
                </div>
              ))}
              <div className="text-[10px] text-muted-foreground pt-1">{unm.toFixed(1)}% unclassified (cash, bonds, foreign listings, unmapped)</div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-xs space-y-1" data-testid="ria-hh-funds">
            <SectionTitle>Funds in the household</SectionTitle>
            {Object.entries(H.funds).map(([f, d]) => (
              <div key={f} className="flex justify-between"><span><span className="font-mono font-bold">{f}</span> <span className="text-muted-foreground">{SHORT[f]}</span></span><span className="font-mono">{usd(d)}</span></div>
            ))}
          </CardContent></Card>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground">Real: each fund's holdings weights (SEC N-PORT, latest filing). Sample: household, amounts, 10% policy limit. Not investment or tax advice.</div>
    </div>
  );
}
